import React, { useContext, useEffect, useState } from 'react'
import ky from 'ky'
import toast from 'react-hot-toast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faPlus, faSpinner, faUserGear } from '@fortawesome/free-solid-svg-icons'
import { GlobalContext } from '../App'
import { API_URL } from '../Globals'
import { AppPermissionId, manageablePermissionGroups, manageablePermissions, togglePermission } from '../Permissions/PermissionDefinitions'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, passwordStrengthLabel, validateNewPassword } from './PasswordPolicy'

interface ManagedUser {
  _id: string
  username: string
  firstName: string
  lastName: string
  permissions: string[]
  disabled?: boolean
}

interface NewUserDraft {
  username: string
  firstName: string
  lastName: string
  permissions: string[]
}

const emptyNewUser = (): NewUserDraft => ({
  username: '',
  firstName: '',
  lastName: '',
  permissions: []
})

const PermissionEditor: React.FC<{ permissions: string[], onToggle: (permission: AppPermissionId) => void }> = ({ permissions, onToggle }) => (
  <fieldset className="rounded-xl border border-gray-200 p-4 sm:col-span-2">
    <legend className="px-1 text-sm font-bold text-gray-700">Käyttöoikeudet</legend>
    <p className="mb-4 text-sm text-gray-500">Valitse vain ne toiminnot, joita käyttäjä tarvitsee. Tarvittavat katseluoikeudet valitaan automaattisesti.</p>
    <div className="space-y-5">
      {manageablePermissionGroups.map(group => (
        <section key={group.title} aria-labelledby={`permission-group-${group.title}`}>
          <h3 id={`permission-group-${group.title}`} className="font-bold text-gray-800">{group.title}</h3>
          <p className="mb-2 text-sm text-gray-500">{group.description}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {group.permissions.map(permission => (
              <label key={permission.id} className={`flex min-h-20 cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${permissions.includes(permission.id) ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                <input type="checkbox" checked={permissions.includes(permission.id)} onChange={() => onToggle(permission.id)} className="mt-0.5 h-5 w-5 flex-none rounded border-gray-300 text-blue-600" />
                <span>
                  <span className="block font-semibold text-gray-800">{permission.label}</span>
                  <span className="mt-0.5 block text-sm font-normal leading-snug text-gray-500">{permission.description}</span>
                </span>
              </label>
            ))}
          </div>
        </section>
      ))}
    </div>
  </fieldset>
)

const UserAccounts: React.FC = () => {
  const { globalVariable } = useContext(GlobalContext)
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [selected, setSelected] = useState<ManagedUser | null>(null)
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [newUser, setNewUser] = useState<NewUserDraft>(emptyNewUser)
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const headers = {
    Authorization: `Bearer ${globalVariable.sessionToken || ''}`,
    Token: localStorage.getItem('token') || ''
  }

  useEffect(() => {
    ky.get(`${API_URL}/api/user/all`, { headers }).json<ManagedUser[]>()
      .then(setUsers)
      .catch(() => setError('Käyttäjätilejä ei voitu ladata.'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changePermission = (permission: AppPermissionId) => {
    if (!selected) return
    setSelected({
      ...selected,
      permissions: togglePermission(selected.permissions, permission)
    })
  }

  const changeNewUserPermission = (permission: AppPermissionId) => {
    setNewUser(current => ({
      ...current,
      permissions: togglePermission(current.permissions, permission)
    }))
  }

  const closeCreateForm = () => {
    setIsCreating(false)
    setNewUser(emptyNewUser())
    setNewPassword('')
    setNewPasswordConfirmation('')
  }

  const createUser = async () => {
    const username = newUser.username.trim()
    const firstName = newUser.firstName.trim()
    const lastName = newUser.lastName.trim()
    if (!username || !firstName || !lastName) {
      toast.error('Täytä käyttäjänimi, etunimi ja sukunimi.')
      return
    }

    const validationError = !newPassword && !newPasswordConfirmation
      ? 'Salasana vaaditaan.'
      : validateNewPassword(newPassword, newPasswordConfirmation)
    if (validationError) {
      toast.error(validationError)
      return
    }

    setCreating(true)
    try {
      const created = await ky.post(`${API_URL}/api/user`, {
        headers,
        json: {
          username,
          firstName,
          lastName,
          password: newPassword,
          permissions: newUser.permissions
        }
      }).json<ManagedUser>()
      setUsers(current => [...current, created].sort((left, right) => (
        `${left.firstName} ${left.lastName}`.localeCompare(`${right.firstName} ${right.lastName}`, 'fi')
      )))
      closeCreateForm()
      toast.success(`Käyttäjä ${created.username} luotiin`)
    } catch (requestError: any) {
      const responseBody = await requestError?.response?.json?.().catch(() => null)
      toast.error(typeof responseBody?.error === 'string' ? responseBody.error : 'Käyttäjätilin luominen epäonnistui.')
    } finally {
      setCreating(false)
    }
  }

  const saveUser = async () => {
    if (!selected) return
    const passwordError = validateNewPassword(password, passwordConfirmation)
    if (passwordError) {
      toast.error(passwordError)
      return
    }

    setSaving(true)
    try {
      const updated = await ky.post(`${API_URL}/api/user/update/${selected._id}`, {
        headers,
        json: {
          username: selected.username,
          firstName: selected.firstName,
          lastName: selected.lastName,
          permissions: selected.permissions,
          disabled: Boolean(selected.disabled),
          ...(password ? { password } : {})
        }
      }).json<ManagedUser>()
      setUsers(current => current.map(user => user._id === updated._id ? updated : user))
      setSelected(updated)
      setPassword('')
      setPasswordConfirmation('')
      if (password && selected.username === globalVariable.username) {
        toast.success('Salasana vaihdettu. Kirjaudu uudelleen.')
        window.setTimeout(() => {
          localStorage.clear()
          window.location.reload()
        }, 900)
      } else {
        toast.success(password ? 'Käyttäjätili ja salasana päivitetty' : 'Käyttäjätili päivitetty')
      }
    } catch (requestError: any) {
      const responseBody = await requestError?.response?.json?.().catch(() => null)
      const message = typeof responseBody?.error === 'string'
        ? responseBody.error
        : 'Käyttäjätilin tallennus epäonnistui.'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const passwordError = validateNewPassword(password, passwordConfirmation)
  const passwordStrength = passwordStrengthLabel(password)
  const newUserPasswordError = !newPassword && !newPasswordConfirmation ? 'Salasana vaaditaan.' : validateNewPassword(newPassword, newPasswordConfirmation)
  const newUserPasswordStrength = passwordStrengthLabel(newPassword)

  return (
    <main className="container mx-auto mb-10 px-3 sm:px-6 lg:px-8">
      <header className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-600"><FontAwesomeIcon icon={faUserGear} /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Käyttäjätilit</h1>
              <p className="mt-1 text-sm text-gray-500">Luo ja muokkaa käyttäjätilejä tai ota tili pois käytöstä. Tilejä ei poisteta.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setSelected(null); setIsCreating(true) }}
            disabled={isCreating}
            className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-default disabled:opacity-60"
          >
            <FontAwesomeIcon icon={faPlus} />
            Uusi käyttäjä
          </button>
        </div>
      </header>

      {isCreating && (
        <section className="mb-5 rounded-2xl border border-blue-200 bg-white shadow-sm" aria-labelledby="create-user-title">
          <div className="border-b border-gray-200 bg-blue-50 px-4 py-4 sm:px-6">
            <h2 id="create-user-title" className="text-xl font-bold text-blue-800">Luo uusi käyttäjätili</h2>
            <p className="mt-1 text-sm text-blue-700/75">Uusi tili on heti käytössä tallentamisen jälkeen.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
            <label className="text-sm font-semibold text-gray-700 sm:col-span-2">Käyttäjänimi<input value={newUser.username} maxLength={100} onChange={event => setNewUser(current => ({ ...current, username: event.target.value }))} autoComplete="off" className="account-field" required /></label>
            <label className="text-sm font-semibold text-gray-700">Etunimi<input value={newUser.firstName} maxLength={100} onChange={event => setNewUser(current => ({ ...current, firstName: event.target.value }))} autoComplete="off" className="account-field" required /></label>
            <label className="text-sm font-semibold text-gray-700">Sukunimi<input value={newUser.lastName} maxLength={100} onChange={event => setNewUser(current => ({ ...current, lastName: event.target.value }))} autoComplete="off" className="account-field" required /></label>

            <section className="rounded-xl border border-gray-200 bg-gray-50 p-4 sm:col-span-2" aria-labelledby="new-user-password-title">
              <h3 id="new-user-password-title" className="font-bold text-gray-800">Aloitussalasana</h3>
              <p className="mt-1 text-sm text-gray-500">Anna salasana käyttäjälle turvallista kanavaa pitkin.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-semibold text-gray-700">Salasana<input type="password" value={newPassword} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setNewPassword(event.target.value)} placeholder={`Vähintään ${PASSWORD_MIN_LENGTH} merkkiä`} autoComplete="new-password" className="account-field" required /></label>
                <label className="text-sm font-semibold text-gray-700">Vahvista salasana<input type="password" value={newPasswordConfirmation} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setNewPasswordConfirmation(event.target.value)} placeholder="Kirjoita salasana uudelleen" autoComplete="new-password" className="account-field" required /></label>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className="text-gray-500">{PASSWORD_MIN_LENGTH}–{PASSWORD_MAX_LENGTH} merkkiä. Kaikki merkit ja välilyönnit ovat sallittuja.</p>
                {newUserPasswordStrength && <p className={`font-bold ${newUserPasswordStrength.color}`}>{newUserPasswordStrength.label}</p>}
              </div>
              {(newPassword || newPasswordConfirmation) && newUserPasswordError && <p role="alert" className="mt-2 text-sm font-semibold text-red-600">{newUserPasswordError}</p>}
            </section>

            <PermissionEditor permissions={newUser.permissions} onToggle={changeNewUserPermission} />
          </div>
          <div className="grid gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 sm:px-6">
            <button type="button" onClick={createUser} disabled={creating || Boolean(newUserPasswordError)} className="min-h-12 rounded-xl bg-blue-600 px-5 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">{creating ? <><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Luodaan</> : 'Luo käyttäjä'}</button>
            <button type="button" onClick={closeCreateForm} disabled={creating} className="min-h-12 rounded-xl border border-gray-300 bg-white px-5 font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60">Peruuta</button>
          </div>
        </section>
      )}

      {selected && (
        <section className="mb-5 rounded-2xl border border-blue-200 bg-white shadow-sm" aria-labelledby="edit-user-title">
          <div className="border-b border-gray-200 bg-blue-50 px-4 py-4 sm:px-6">
            <h2 id="edit-user-title" className="text-xl font-bold text-blue-800">Muokkaa käyttäjää {selected.username}</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
            <label className="text-sm font-semibold text-gray-700 sm:col-span-2">Käyttäjänimi<input value={selected.username} maxLength={100} onChange={event => setSelected({ ...selected, username: event.target.value })} className="account-field" /></label>
            <label className="text-sm font-semibold text-gray-700">Etunimi<input value={selected.firstName} onChange={event => setSelected({ ...selected, firstName: event.target.value })} className="account-field" /></label>
            <label className="text-sm font-semibold text-gray-700">Sukunimi<input value={selected.lastName} onChange={event => setSelected({ ...selected, lastName: event.target.value })} className="account-field" /></label>

            <section className="rounded-xl border border-gray-200 bg-gray-50 p-4 sm:col-span-2" aria-labelledby="password-change-title">
              <h3 id="password-change-title" className="font-bold text-gray-800">Vaihda salasana</h3>
              <p className="mt-1 text-sm text-gray-500">Jätä kentät tyhjiksi, jos salasanaa ei vaihdeta. Vaihto katkaisee käyttäjän aiemmat istunnot.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-semibold text-gray-700">Uusi salasana<input type="password" value={password} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setPassword(event.target.value)} placeholder={`Vähintään ${PASSWORD_MIN_LENGTH} merkkiä`} autoComplete="new-password" className="account-field" /></label>
                <label className="text-sm font-semibold text-gray-700">Vahvista uusi salasana<input type="password" value={passwordConfirmation} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setPasswordConfirmation(event.target.value)} placeholder="Kirjoita salasana uudelleen" autoComplete="new-password" className="account-field" /></label>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className="text-gray-500">{PASSWORD_MIN_LENGTH}–{PASSWORD_MAX_LENGTH} merkkiä. Kaikki merkit ja välilyönnit ovat sallittuja.</p>
                {passwordStrength && <p className={`font-bold ${passwordStrength.color}`}>{passwordStrength.label}</p>}
              </div>
              {passwordError && <p role="alert" className="mt-2 text-sm font-semibold text-red-600">{passwordError}</p>}
            </section>

            <PermissionEditor permissions={selected.permissions} onToggle={changePermission} />

            <label className="flex min-h-12 items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 font-semibold text-gray-700 sm:col-span-2">
              <input type="checkbox" checked={!selected.disabled} disabled={selected.username === globalVariable.username} onChange={event => setSelected({ ...selected, disabled: !event.target.checked })} className="h-5 w-5 rounded border-gray-300 text-blue-600" />
              Tili käytössä
              {selected.username === globalVariable.username && <span className="text-xs font-normal text-gray-400">Omaa tiliä ei voi poistaa käytöstä</span>}
            </label>
          </div>
          <div className="grid gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 sm:px-6">
            <button onClick={saveUser} disabled={saving || Boolean(passwordError)} className="min-h-12 rounded-xl bg-blue-600 px-5 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">{saving ? <><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Tallennetaan</> : 'Tallenna'}</button>
            <button onClick={() => { setSelected(null); setPassword(''); setPasswordConfirmation('') }} className="min-h-12 rounded-xl border border-gray-300 bg-white px-5 font-semibold text-gray-700 hover:bg-gray-100">Sulje</button>
          </div>
        </section>
      )}

      {loading ? (
        <div className="flex justify-center py-12 text-blue-600"><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Ladataan käyttäjiä…</div>
      ) : error ? (
        <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center font-medium text-red-700">{error}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {users.map(user => (
            <article key={user._id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{user.firstName} {user.lastName}</h2>
                  <p className="text-sm text-gray-500">{user.username}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${user.disabled ? 'bg-gray-200 text-gray-600' : 'bg-emerald-100 text-emerald-700'}`}>{user.disabled ? 'Pois käytöstä' : 'Käytössä'}</span>
              </div>
              <div className="mt-3 flex min-h-7 flex-wrap gap-2">
                {user.permissions.length > 0 ? user.permissions.map(permission => <span key={permission} className="rounded-lg bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">{manageablePermissions.find(item => item.id === permission)?.label || permission}</span>) : <span className="text-sm text-gray-400">Ei käyttöoikeuksia</span>}
              </div>
              <button onClick={() => { closeCreateForm(); setSelected({ ...user, permissions: [...user.permissions] }); setPassword(''); setPasswordConfirmation(''); window.scrollTo({ top: 0, behavior: 'smooth' }) }} className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 font-semibold text-blue-700 hover:bg-blue-100"><FontAwesomeIcon icon={faPen} />Hallinnoi</button>
            </article>
          ))}
        </div>
      )}
    </main>
  )
}

export default UserAccounts
